// Third Party
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

// UI
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Icons
import { ArrowLeft, Download } from "lucide-react";

// Services
import { getActionPlanByYearly } from "@/services/action-plan";
import { S3_BUCKET_LINK } from "@/components/constants";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

type FinPlanDetail = {
  id: string;
  financialPlanId: string;
  financialType: "INCOME" | "EXPENSE";
  index: number | null;
  detailName: string;
  amount: number;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

type PlanYearly = {
  id: string;
  companyId: string;
  yearly: number;
  planName: string;
  totalIncome: number;
  totalExpense: number;
  totalProfit: number;
  docFile: string;
  description: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  finPlanDetails: FinPlanDetail[];
};

export const Route = createFileRoute(
  "/_layout-private/report/action-plan/preview"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      yearly: search.yearly as string | undefined,
    };
  },
});

function RouteComponent() {
  const { yearly } = Route.useSearch();
  const companyId = JSON.parse(localStorage.getItem("companyId") || "");
  const navigate = useNavigate();
  const { operationPlan } = useAccessPermission();
  // Query
  const { data, isLoading } = useQuery({
    queryKey: ["action-plan", "preview", yearly],
    queryFn: () =>
      getActionPlanByYearly({
        companyId,
        yearly: parseInt(yearly || "0"),
      }),
    enabled: !!yearly,
  });

  const actionPlan = data?.data as PlanYearly;
  const incomeDetail = actionPlan?.finPlanDetails.filter(
    (detail) => detail.financialType === "INCOME"
  );
  const expenseDetail = actionPlan?.finPlanDetails.filter(
    (detail) => detail.financialType === "EXPENSE"
  );

  // const formatAmount = (amount: number, type: 'INCOME' | 'EXPENSE') => {
  //     const formattedNumber = amount.toLocaleString();
  //     return type === 'EXPENSE' ? `(${formattedNumber})` : formattedNumber;
  // };

  const formatAmount = (amount: number, type: "INCOME" | "EXPENSE") => {
    // Convert to positive number for formatting
    const absoluteAmount = Math.abs(amount);
    const formattedNumber = absoluteAmount.toLocaleString();

    // For EXPENSE type or negative numbers, wrap in parentheses
    return type === "EXPENSE" ? `(${formattedNumber})` : formattedNumber;
  };

  if (isLoading) {
    return <DinamixSkeletonTable />;
  }

  console.log("Data", data);

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link
                replace
                to="/report/action-plan"
                search={{ page: 1, startYear: 0, endYear: 0 }}
              >
                ລາຍງານແຜນດຳເນີນງານ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ສ້າງລາຍງານແຜນດຳເນີນງານ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/action-plan",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex gap-1 items-center mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <CardTitle>ລາຍລະອຽດແຜນດຳເນີນງານ {yearly}</CardTitle>
          <CardDescription>ໃບລາຍງານແຜນດຳເນີນງານ</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2 mb-4">
            <Label htmlFor="planName">ຊື່ເອກະສານ</Label>
            <Input
              id="planName"
              name="planName"
              type="text"
              value={actionPlan?.planName}
              className="w-[200px]"
              readOnly
            />
          </div>

          <div className="flex flex-col gap-2 mb-4">
            <Label htmlFor="planName">ປີ</Label>
            <Input
              id="yearly"
              name="yearly"
              type="number"
              value={actionPlan?.yearly}
              className="w-[200px]"
              readOnly
            />
          </div>

          <Table>
            <TableHeader>
              <TableRow className="[&>*]:py-5 [&>*]:font-bold [&>*]:text-center [&>*]:text-black [&>*]:bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                <TableHead>ເນື້ອໃນ</TableHead>
                <TableHead className="rounded-tr-lg">ແຜນການ</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <TableRow className="bg-[#216C4C33]">
                <TableCell colSpan={2} className="font-bold">
                  I. ລາຍຮັບ
                </TableCell>
              </TableRow>
              {incomeDetail?.map((detail) => (
                <TableRow key={detail.id} className="[&>*]:border">
                  <TableCell className="text-left">
                    {detail.detailName}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatAmount(detail.amount, "INCOME")}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-[#216C4C33]">
                <TableCell colSpan={3} className="font-bold">
                  II. ລາຍຈ່າຍ
                </TableCell>
              </TableRow>
              {expenseDetail?.map((detail) => (
                <TableRow key={detail.id} className="[&>*]:border">
                  <TableCell className="text-left">
                    {detail.detailName}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatAmount(detail.amount, "EXPENSE")}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-[#216C4C33]">
                <TableCell className="font-bold">
                  III. ຜົນໄດ້ຮັບກ່ອນອາກອນ
                </TableCell>
                <TableCell className="text-right">
                  {formatAmount(
                    actionPlan?.totalIncome - actionPlan?.totalExpense,
                    actionPlan?.totalIncome - actionPlan?.totalExpense < 0
                      ? "EXPENSE"
                      : "INCOME"
                  )}
                </TableCell>
              </TableRow>
              <TableRow className="bg-[#216C4C33]">
                <TableCell className="font-bold">
                  IV. ຜົນໄດ້ຮັບ (ກຳໄລ)
                </TableCell>
                <TableCell className="text-right">
                  {formatAmount(
                    actionPlan?.totalProfit,
                    actionPlan?.totalProfit < 0 ? "EXPENSE" : "INCOME"
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {actionPlan?.docFile
            ? operationPlan.isDownload && (
                <div className="flex justify-start mt-4">
                  <a
                    href={S3_BUCKET_LINK + actionPlan?.docFile}
                    download={S3_BUCKET_LINK + actionPlan?.docFile}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button className="flex gap-2 items-center">
                      <Download className="w-4 h-4" />
                      <span>ດາວໂຫຼດເອກະສານຕິດຄັດ</span>
                    </Button>
                  </a>
                </div>
              )
            : null}
        </CardContent>
      </Card>
    </>
  );
}
